import { cloneDeep, defaultsDeep, uniq } from 'lodash'

import {
  SUPPLIER_ORDER_HEADER_INITIAL_STATE,
  updateSupplierOrderHeaderAmounts,
} from './supplier-order-header.lib'
import {
  addSupplierOrderRows,
  removeSupplierOrderRow,
  updateSupplierOrderRowAmounts,
} from './supplier-order-rows.lib'
import { DeepPartial } from '../../../models/util.model'
import { ProductSelection } from '../../products'
import { Tenant } from '../../tenants'
import {
  SupplierOrder,
  SupplierOrderRow,
} from '../models/supplier-order.entity'

// Initial states

const SUPPLIER_ORDER_INITIAL_STATE: DeepPartial<SupplierOrder> = {
  status: 'DRAFTED',
  header: SUPPLIER_ORDER_HEADER_INITIAL_STATE,
  rows: [],
  productsCount: 0,
  rowsCount: 0,
}

// Init

export function initSupplierOrder(
  supplierOrder: Partial<SupplierOrder> = {},
): SupplierOrder {
  return defaultsDeep(cloneDeep(supplierOrder), SUPPLIER_ORDER_INITIAL_STATE)
}

export function setSupplierOrderDefaults(
  supplierOrder: SupplierOrder,
  defaults: DeepPartial<SupplierOrder>,
): SupplierOrder {
  return defaultsDeep(cloneDeep(supplierOrder), defaults)
}

// Amounts

export function updateSupplierOrderAmounts(
  supplierOrder: SupplierOrder,
): SupplierOrder {
  const rows = supplierOrder.rows.map((row) =>
    updateSupplierOrderRowAmounts(row),
  )
  const header = updateSupplierOrderHeaderAmounts(supplierOrder.header, rows)

  const productsCount = rows.reduce((acc, row) => acc + row.orderedQty, 0)
  const rowsCount = rows.length

  return {
    ...supplierOrder,
    header,
    rows,
    productsCount,
    rowsCount,
  }
}

// Rows

export function addSupplierOrderProducts(
  supplierOrder: SupplierOrder,
  selection: ProductSelection[],
): SupplierOrder {
  return updateSupplierOrderAmounts({
    ...supplierOrder,
    rows: addSupplierOrderRows(
      supplierOrder.rows,
      selection,
      supplierOrder.supplierId,
    ),
  })
}

export function deleteSupplierOrderRow(
  supplierOrder: SupplierOrder,
  row: SupplierOrderRow,
): SupplierOrder {
  return updateSupplierOrderAmounts({
    ...supplierOrder,
    rows: removeSupplierOrderRow(supplierOrder.rows, row),
  })
}

export function getSupplierOrderGoodsReceiveIds(
  supplierOrder: SupplierOrder,
): string[] {
  const ids: string[] = []
  supplierOrder.rows.map((r) => {
    r.receptions?.map((re) => {
      if (re._id) {
        ids.push(re._id)
      }
    })
  })
  return uniq(ids)
}

// Products

export function getSupplierOrderProductIds(
  supplierOrder: SupplierOrder,
): string[] {
  const ids: string[] = []
  supplierOrder.rows.map((r) => {
    if (r.product._id) {
      ids.push(r.product._id)
    }
  })
  return uniq(ids)
}

// Defaults

export function getSupplierOrderDefaults(
  tenant: Tenant,
): DeepPartial<SupplierOrder> {
  return {
    warehouseId: tenant?.supplierOrders?.warehouseId,
    header: {
      currency: tenant.defaults?.currency,
    },
  }
}
